import { useState, useEffect } from 'react';
import { Search, Calendar, User } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { supabase } from '../../../../lib/supabase';
import { getMentorAssignments } from '../../data/mentorAssignments';
import { Link } from "react-router-dom";

interface Mentee {
  id: string;
  name: string;
  email: string;
  status: 'pending' | 'active' | 'completed';
  nextSession?: string;
  assignmentStart?: string;
  assignmentEnd?: string | null;
}

export function MenteesList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [mentees, setMentees] = useState<Mentee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMentees = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Get the current user's session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setError('You must be logged in to view this page');
          return;
        }
        
        // Get the current user's ID
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          throw new Error('User not authenticated');
        }
        
        // Fetch mentor assignments
        const result = await getMentorAssignments(user.id);
        
        if (!result.success) {
          throw new Error(result.error || 'Failed to fetch mentees');
        }
        
        if (!result.data || result.data.length === 0) {
          setMentees([]);
        } else {
          // Transform assignments to mentees format
          const menteesList: Mentee[] = result.data.map(assignment => ({
            id: assignment.mentee.uuid,
            name: assignment.mentee.name,
            email: assignment.mentee.email,
            status: 'active', // Default to active for now
            assignmentStart: assignment.start,
            assignmentEnd: assignment.end
          }));
          
          setMentees(menteesList);
        }
      } catch (err) {
        console.error('Error fetching mentees:', err);
        setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    fetchMentees();
  }, []);

  const filteredMentees = mentees.filter(mentee =>
    mentee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    mentee.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: Mentee['status']) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">My Mentees</h1>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vincer-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your mentees...</p>
        </div>
      ) : error ? (
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Try Again
          </button>
        </div>
      ) : mentees.length === 0 ? (
        <div className="text-center py-12">
          <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
            <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Mentees Assigned</h3>
            <p className="text-gray-600 mb-4">You don't have any mentees assigned to you yet.</p>
            <p className="text-sm text-gray-500">Check back later or contact support if you believe this is an error.</p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search mentees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-vincer-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="divide-y">
            {filteredMentees.map((mentee) => (
              <div key={mentee.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-medium mb-1">{mentee.name}</h3>
                    <p className="text-gray-600">{mentee.email}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(mentee.status)}`}>
                    {mentee.status.charAt(0).toUpperCase() + mentee.status.slice(1)}
                  </span>
                </div>

                <div className="flex items-center gap-6">
                  {mentee.assignmentStart && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4" />
                      <span>
                        Assigned since: {format(parseISO(mentee.assignmentStart), 'MMM d, yyyy')}
                        {mentee.assignmentEnd && ` until ${format(parseISO(mentee.assignmentEnd), 'MMM d, yyyy')}`}
                      </span>
                    </div>
                  )}
                </div>
                <Link to={`/mentor/pre-session?sessionId=${mentee.id}`}>
                  <button className="text-vincer-500 hover:text-vincer-400 font-medium">
                    View Details
                  </button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}